import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Plus, ArrowRight, TrendingUp, Shield } from 'lucide-react';
import { Button } from './ui/button';

/**
 * First-Return (Day 2+) Experience
 * 
 * A supportive, reassuring experience for returning users.
 * 
 * Design principles:
 * - Calm, trust-building tone
 * - Reinforce progress without pressure
 * - Emphasise user control and autonomy
 * - No predictions, health advice, or anxiety-inducing language
 * - No gamification (streaks, scores, rewards)
 * 
 * Two variants based on data:
 * - Full version (0-1 results): Reassurance + clarity + emotional safety
 * - Compact version (2+ results): Momentum without ceremony
 */

// ============================================
// MESSAGING STRINGS - Easy to adjust later
// ============================================
const MESSAGES = {
  // Full version (0-1 results)
  full: {
    greeting: (name) => `Welcome back, ${name}`,
    subheading: (hasResults) => hasResults 
      ? 'Your baseline is saved. You can add more whenever you have new results.'
      : 'Ready to start tracking? Add your first blood result when you\'re ready.',
    confirmationTitle: (hasResults) => hasResults 
      ? 'Your results are safely stored'
      : 'Start tracking your health journey',
    confirmationBody: (hasResults) => hasResults
      ? 'Over time, each entry helps build a clearer picture. There\'s no rush — add results at your own pace.'
      : 'Adding your blood results helps you see trends over time. There\'s no rush — start whenever you\'re ready.',
    primaryAction: (hasResults) => hasResults ? 'Add another result' : 'Add your first result',
    secondaryAction: 'Learn how this works',
    reassurance: 'You\'re in control. Nothing happens without your say.',
  },
  // Compact version (2+ results)
  compact: {
    headline: 'Your tracking is coming along',
    subtext: 'Each result adds a little more clarity over time.',
    action: 'Add result',
  },
};

const FirstReturnExperience = ({ userName, onDismiss, bloodResultCount = 0 }) => {
  const navigate = useNavigate();
  const firstName = userName?.split(' ')[0] || 'there';
  const hasMultipleResults = bloodResultCount >= 2;
  const hasAnyResults = bloodResultCount >= 1;

  const handleAddResult = () => {
    onDismiss();
    navigate('/blood-results');
  };

  const handleLearnMore = () => {
    onDismiss();
    navigate('/how-to-use');
  };

  // ============================================
  // VARIANT B: Compact Version (2+ results)
  // Purpose: Momentum without ceremony
  // ============================================
  if (hasMultipleResults) {
    return (
      <div className="mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-teal-50 rounded-full flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-teal-600" />
            </div>
            <div>
              <p className="font-medium text-slate-800 text-sm">
                {MESSAGES.compact.headline}
              </p>
              <p className="text-slate-500 text-xs">
                {MESSAGES.compact.subtext}
              </p>
            </div>
          </div>
          <Button
            onClick={handleAddResult}
            size="sm"
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg flex items-center gap-1.5 flex-shrink-0"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">{MESSAGES.compact.action}</span>
          </Button>
        </div>
      </div>
    );
  }

  // ============================================
  // VARIANT A: Full Version (0-1 results)
  // Purpose: Reassurance, clarity, emotional safety
  // ============================================
  return (
    <div className="mb-8">
      {/* Welcome Banner - Calm, supportive greeting */}
      <div className="bg-gradient-to-r from-teal-50 to-slate-50 rounded-2xl p-6 md:p-8 border border-teal-100/50 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm border border-slate-100">
            <TrendingUp className="h-6 w-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-1">
              {MESSAGES.full.greeting(firstName)}
            </h2>
            <p className="text-slate-600">
              {MESSAGES.full.subheading(hasAnyResults)}
            </p>
          </div>
        </div>
      </div>

      {/* Confirmation Card - Clear, reassuring */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-6">
        <div className="flex items-start gap-4 mb-5">
          <div className="flex-shrink-0 w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
            <Check className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">
              {MESSAGES.full.confirmationTitle(hasAnyResults)}
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              {MESSAGES.full.confirmationBody(hasAnyResults)}
            </p>
          </div>
        </div>

        {/* Primary Action - Inviting, not pushy */}
        <Button
          onClick={handleAddResult}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3.5 rounded-xl font-medium text-base flex items-center justify-center gap-2"
        >
          <Plus className="h-5 w-5" />
          {MESSAGES.full.primaryAction(hasAnyResults)}
        </Button>

        {/* Secondary Link - Optional, low-pressure */}
        <button
          onClick={handleLearnMore}
          className="w-full mt-3 py-2 text-slate-500 text-sm hover:text-teal-700 transition-colors flex items-center justify-center gap-1"
        >
          {MESSAGES.full.secondaryAction}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {/* Reassurance Footer - Autonomy and control */}
      <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
        <Shield className="h-4 w-4" />
        <span>{MESSAGES.full.reassurance}</span>
      </div>
    </div>
  );
};

export default FirstReturnExperience;
